import React, { useEffect, useState } from 'react';
import { GetExcelHeadersByOption } from '../../reduxToolkit/Files/FilesApi';
import { useDispatch } from 'react-redux';
import pdf_image from '../../assets/images/pdf.svg';
import ic_excel from '../../assets/images/ic-excel.svg';
import OptionModal from '../../pages/Modals/OptionModal';
import ButtonDropdown from "./ButtonDropdown";
import {useTranslation} from "react-i18next";
import {ExportIcon} from "./IconButtons";
/**
 * @email kpozos@ibl.mx
 * @author Karen Yunuen Pozos Arias
 * @title Full Stack Developer
 *
 * ExportDataBtn component renders a dropdown button that allows users to export data
 * in either Excel or PDF format. It optionally supports a custom API call for exporting.
 *
 * @component
 * @param {String} [id='btn_edb_export'] - The HTML id attribute for the export button (required).
 * @param {Object} body - The body data passed to the modal when exporting (required).
 * @param {String} option - The selected export module name (required).
 * @param {string} [searchElement] - Optional search input value used for export filtering (required).
 * @param {Number} moduleId - Module identifier for which the export is being performed (required).
 * @param {boolean} [exportAttendance] - Flag to indicate if the export is for attendance (required).
 * @param {boolean} [search] - Flag used in the export modal to identifier if search field has value (required).
 * @param {function} [callApi=null] - Custom function to execute instead of the default API call. It receives `isExcel` as parameter (optional).
 * @param {String} [classes='btn-icon-export'] Additional CSS classes applied to the dropdown button for styling.
 * @param {boolean} [showHeadersModal=true] - Flag that controls whether the export headers modal.
 * @param state setDisplayModal: State management to ensure only one modal is visible at a time.
 * is shown when using a custom API function (`callApi`)
 * @param contractorSupplier Flag that determines whether contractorId and supplierId
 * are included in the export request body. When true, both identifiers are set and sent to the export modal.
 * @returns {JSX.Element} A button dropdown with Excel and PDF options and a modal for configuration.
 */

const ExportDataBtn = ({
                           id='btn_edb_export',
                           body,
                           option,
                           searchElement,
                           moduleId,
                           exportAttendance,
                           search,
                           callApi = null,
                           showHeadersModal=true,
                           classes='btn-icon-export',
                           contractorSupplier= true,
                           setDisplayModal= null
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [showModal, setShowModal] = useState(false);
    const [excel, setExcel] = useState(false);

    /**
     * Handles the default export action using the standard endpoint to get Excel or PDF headers.
     * This function is triggered when no custom API call (`callApi`) is provided.
     *
     * @function
     * @param {boolean} isExcel - Flag indicating whether the export should be in Excel format (true) or PDF format (false).
     * @returns {void}
     */
    const handleDownloadOption = (isExcel) => {
        setExcel(isExcel);
        setShowModal(true);
        dispatch(GetExcelHeadersByOption({ option: option.toString(), isExcel }));
    };

    /**
     * Handles export when a custom API function is provided through the `callApi` prop.
     * Opens the modal and passes the format type to the custom export function.
     *
     * @function
     * @param {boolean} isExcel - Flag indicating whether the export should be in Excel format (true) or PDF format (false).
     * @returns {void}
     */
    const handleCustomDownload = (isExcel) => {
        setExcel(isExcel);
        if (showHeadersModal) setShowModal(true);
        callApi(isExcel);
    };

    /**
     * Hides other active modals when the export modal is opened,
     * ensuring only one modal is visible at a time.
     */
    useEffect(() => {
        if (showModal && setDisplayModal) {
            setDisplayModal('d-none');
        }
    }, [showModal]);

    return (
        <>
            <ButtonDropdown
                id={id}
                classes={classes}
                trans={'export_data'}
                spliName={false}
                icon={ExportIcon}
                options={[
                    {
                        id: 'mim_edb_excel',
                        option: t('Excel'),
                        img: ic_excel,
                        action: () => (callApi !== null ? handleCustomDownload(true) : handleDownloadOption(true)),
                    },
                    {
                        id: 'mim_edb_pdf',
                        option: t('PDF'),
                        img: pdf_image,
                        action: () => (callApi !== null ? handleCustomDownload(false) : handleDownloadOption(false)),
                    },
                ]}
            />

            {showModal && (
                <OptionModal
                    body={body}
                    moduleId={moduleId}
                    show={showModal}
                    onHide={() => {
                        setShowModal(false);
                        if (setDisplayModal) setDisplayModal('');
                    }}
                    excel={excel}
                    option={option}
                    searchElement={searchElement}
                    exportAttendance={exportAttendance}
                    search={search}
                    contractorSupplier={contractorSupplier}
                />
            )}
        </>
    );
};

export default ExportDataBtn;
